/**
 * advisorOnboarding — Lesen des eigenen §5-Onboarding-Status (B4-Vorprüfung).
 *
 * Die Accept-Flows (Richtung a1 client-self-update, a2 Token-Accept) müssen VOR dem
 * Accept prüfen, ob die eigene Kanzlei-Onboarding-Bestätigung
 * (`advisor_profiles/{uid}.onboardingConfirmedAt` + `onboardingVersion`) vorliegt und
 * aktuell ist — sonst blockt der Server (E3/Preconditions) bzw. die Rules den Read.
 * Rules erlauben den get-own-Read (`advisor_profiles/{uid}`, firestore.rules).
 *
 * Fail-safe: jeder Fehler ⇒ „nicht bestätigt" (der §5-Dialog wird gezeigt; im
 * schlimmsten Fall bestätigt die Kanzlei einmal zu viel — nie zu wenig).
 *
 * @module utils/advisorOnboarding
 */

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import logger from './logger';
import { KANZLEI_ONBOARDING_VERSION } from '../config/advisorConsentAccess';

/**
 * Grandfather-Literal (Gegenstück zu `GRANDFATHER_VERSION` in
 * `functions/src/access/advisorConsent.ts`) — der Server akzeptiert es NEBEN der
 * aktuellen Version als gültig; die FE-Vorprüfung ebenso, damit ein bereits
 * grandfathered Beta-Account nicht unnötig re-bestätigen muss.
 */
const GRANDFATHER_VERSION = 'grandfathered-beta-2026-07';

/** Ergebnis der eigenen Onboarding-Vorprüfung. */
export interface AdvisorOnboardingStatus {
  /** `onboardingConfirmedAt` vorhanden (irgendeine Version). */
  present: boolean;
  /** vorhanden UND Version aktuell (oder grandfathered) ⇒ kein §5-Dialog nötig. */
  confirmed: boolean;
}

/**
 * Liest den §5-Onboarding-Status des eigenen `advisor_profiles`-Dokuments.
 *
 * @param uid uid des aktuellen Nutzers (Kanzlei/Advisor).
 * @returns {@link AdvisorOnboardingStatus}; fail-safe `{present:false,confirmed:false}`.
 */
export async function readOwnOnboardingStatus(
  uid: string,
): Promise<AdvisorOnboardingStatus> {
  try {
    const snap = await getDoc(doc(db, 'advisor_profiles', uid));
    const data = snap.exists() ? snap.data() : null;
    const present = !!data?.onboardingConfirmedAt;
    const version =
      typeof data?.onboardingVersion === 'string' ? data.onboardingVersion : '';
    const confirmed =
      present &&
      (version === KANZLEI_ONBOARDING_VERSION || version === GRANDFATHER_VERSION);
    return { present, confirmed };
  } catch (err) {
    logger.error('[advisorOnboarding] status read failed', err);
    // Fail-safe: unbekannt ⇒ als „nicht bestätigt" behandeln (Dialog wird gezeigt).
    return { present: false, confirmed: false };
  }
}
